import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import {
    Box,
    Stack,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from "@mui/material"
import { Dispatch, ReactElement, SetStateAction } from "react"

import { LCT_TILES } from "./applicationTypeTiles"
import { useScreenSize } from "../../../../hooks/useScreenSize"
import { GRID_SPACING } from "../../../../themes/defaultThemeParams"
import { scrollToBottom } from "../../../../utils/submitApplication/submitApplicationUtils"
import AppButton from "../../../buttons/appButton"
import TileButton from "../../../buttons/tileButton"
import LctHoneyCombMask from "../../../images/lctHoneyCombMask"
import DisplayIf from "../../../wrappers/displayIf"

interface ISelectLctProps {
    lcts: string[]
    setLcts: Dispatch<SetStateAction<string[]>>
    setDisplaySelectLct: Dispatch<SetStateAction<boolean>>
}

/**
 * Entry point for the application form. Allows user to select the LCT
 * they wish to submit an application for.
 */
const SelectLct = ({
    lcts,
    setLcts,
    setDisplaySelectLct,
}: ISelectLctProps): ReactElement => {
    const theme = useTheme()
    const { isTabletOrMob } = useScreenSize()

    const handleLctChange = (value: string): void => {
        setLcts((prev) =>
            prev && prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
        )
        if (isTabletOrMob) {
            scrollToBottom(document)
        }
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexGrow: 1,
                height: "100%",
                padding: 0,
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={GRID_SPACING}
                width="100%"
            >
                <DisplayIf condition={!isTabletOrMob}>
                    <LctHoneyCombMask maxWidth="45%" />
                </DisplayIf>

                <Stack
                    direction="column"
                    spacing={GRID_SPACING}
                    width="100%"
                    padding={2}
                >
                    <Box>
                        <Typography variant="h3" color="text.primary">
                            What are you installing?
                        </Typography>
                        <Typography
                            variant="h6"
                            gutterBottom
                            color="text.secondary"
                            fontWeight="light"
                        >
                            You can select one or multiple technologies
                        </Typography>
                    </Box>
                    <ToggleButtonGroup
                        value={lcts}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr",
                                lg: "1fr 1fr 1fr",
                            },
                            gridGap: {
                                xs: theme.spacing(1.5),
                                md: theme.spacing(3),
                            },
                        }}
                    >
                        {LCT_TILES.map((lctTile) => (
                            <TileButton
                                key={lctTile.value}
                                title={lctTile.title}
                                value={lctTile.value}
                                Icon={lctTile.icon}
                                onClick={() => handleLctChange(lctTile.value)}
                                selected={lcts.includes(lctTile.value)}
                            />
                        ))}
                    </ToggleButtonGroup>

                    <Stack
                        direction="row"
                        spacing={GRID_SPACING}
                        justifyContent="flex-end"
                        alignItems="flex-end"
                        flexGrow={1}
                    >
                        <AppButton
                            variant="contained"
                            onClick={() => setDisplaySelectLct(false)}
                            endIcon={<NavigateNextIcon />}
                            disabled={!lcts.length}
                        >
                            Get Started
                        </AppButton>
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    )
}

export default SelectLct
